import { ExpandableSearchBar } from '@components/ExpandableSearchBar';
import { PageContainer } from '@components/Layout/PageContainer';
import { PageTitle } from '@components/Layout/PageTitle';
import { ResponsiveButton } from '@components/ResponsiveButton';
import { Add, Web } from '@mui/icons-material';
import { Grid, Stack, Typography } from '@mui/material';
import { deburrSearch } from '@utils/deburrSearch';
import { useGetMe } from '@utils/useGetMe';
import { useResponsive } from '@utils/useResponsive';
import { useGetProjectsQuery } from 'gql/index';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { AddProjectTemplateDialog } from '../components/AddProjectTemplateDialog';
import { ProjectCard } from '../components/ProjectCard';

export const ProjectTemplatesPage: React.FC = () => {
    const { formatMessage } = useIntl();
    const { isMobile } = useResponsive();
    const { me, canCreateProject } = useGetMe();

    const [searchFilter, setSearchFilter] = useState('');
    const [isAddTemplateDialogOpen, setIsAddTemplateDialogOpen] = useState(false);

    const { data: myTemplates } = useGetProjectsQuery({
        filter: {
            and: [
                { isTemplate: { eq: true } },
                { createdById: { eq: me?.id } }
            ]
        }
    }, { select: d => d.projects, placeholderData: prev => prev });

    const { data: sharedTemplates } = useGetProjectsQuery({
        filter: {
            and: [
                { isTemplate: { eq: true } },
                { createdById: { neq: me?.id } }
            ]
        }
    }, { select: d => d.projects, placeholderData: prev => prev });

    const filteredMyTemplates = useMemo(() => (myTemplates ?? []).filter(p => deburrSearch(p.name, searchFilter)), [myTemplates, searchFilter]);
    const filteredSharedTemplates = useMemo(() => (sharedTemplates ?? []).filter(p => deburrSearch(p.name, searchFilter)), [sharedTemplates, searchFilter]);

    return <>
        <PageTitle
            title={formatMessage({ id: 'Project templates' })}
            icon={<Web />}
            actionButton={canCreateProject && (
                <ResponsiveButton color='primary' variant='contained' icon={<Add />} onClick={() => setIsAddTemplateDialogOpen(true)}>
                    {formatMessage({ id: 'Create template' })}
                </ResponsiveButton>
            )}
            backgroundColor='secondary'
        />

        <AddProjectTemplateDialog open={isAddTemplateDialogOpen} onClose={() => setIsAddTemplateDialogOpen(false)} />

        <PageContainer>
            <Stack direction={isMobile ? 'column' : 'row'} justifyContent={'space-between'} flexWrap='wrap' gap={2} mb={4}>
                <ExpandableSearchBar
                    fullWidth={isMobile}
                    sx={{ width: isMobile ? undefined : '50ch' }}
                    value={searchFilter}
                    onChange={e => setSearchFilter(e.target.value)}
                />
            </Stack>

            {filteredMyTemplates.length > 0 && (
                <Typography variant='h6' mb={2}>
                    {formatMessage({ id: 'My templates' })}
                </Typography>
            )}

            <Grid container spacing={2}>
                {filteredMyTemplates.map(p => (
                    <Grid size={{ xs: 12, md: 3 }} key={p.id}>
                        <ProjectCard project={p} />
                    </Grid>
                ))}
            </Grid>

            {filteredSharedTemplates.length > 0 && (
                <Typography variant='h6' mt={4} mb={2}>
                    {formatMessage({ id: 'Shared with me' })}
                </Typography>
            )}

            <Grid container spacing={2}>
                {filteredSharedTemplates.map(p => (
                    <Grid size={{ xs: 12, md: 3 }} key={p.id}>
                        <ProjectCard project={p} />
                    </Grid>
                ))}
            </Grid>
        </PageContainer>
    </>;
};


